import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  LOAN_COMP_REQUIRED_FIELDS,
  LOAN_COMP_TYPES,
  loanCompFieldConfig,
} from "../../config/loanCompFieldConfig";
import { apiPost, apiPut } from "../../services/genesisApi/httpsMethods";
import { useReferenceLists } from "../../context/GlobalGetLists";
import { ConfirmDialog } from "../../components/common/confirmDialog";
import { CustomButton } from "../../components/common/customButton";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import { DynamicForm } from "../../components/common/DynamicForm";
import { ErrorDialog } from "../../components/common/errorDialog";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import {
  buildLoanCompPayload,
  hasRequiredLoanCompFields,
  type LoanCompFormValues,
} from "./loanCompPayload";

const emptyLoanComp: LoanCompFormValues = {
  loan_type: null,
  date: null,
  project_name: null,
  sponsor: null,
  country: null,
  city: null,
  sector: null,
  lender_type: null,
  loan_amount_local: null,
  ltc_ltv: null,
  margin: null,
  irr: null,
  est_local_irr: null,
  comment: null,
};

interface CreateLoanCompProps {
  loanComp?: LoanCompFormValues;
  isEdit?: boolean;
}

const CreateLoanComp = ({
  loanComp: initialLoanComp = emptyLoanComp,
  isEdit = false,
}: CreateLoanCompProps) => {
  const navigate = useNavigate();
  const { countries, sectors } = useReferenceLists();
  const { errorDialog, showError, showErrorFromResponse, hideError } =
    useErrorDialog();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loanComp, setLoanComp] = useState<LoanCompFormValues>(initialLoanComp);
  const loanCompId = typeof loanComp.id === "number" ? loanComp.id : undefined;

  // loan_type is free text in the backend; keep an existing value selectable even
  // if it is not one of the known Market Tracker types.
  const loanTypes = useMemo(() => {
    const current =
      typeof loanComp.loan_type === "string" ? loanComp.loan_type : "";
    return current && !LOAN_COMP_TYPES.includes(current)
      ? [...LOAN_COMP_TYPES, current]
      : LOAN_COMP_TYPES;
  }, [loanComp.loan_type]);

  const handleChange = (field: string, value: unknown) => {
    setLoanComp((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (isEdit && loanCompId === undefined) {
      showError(undefined, "This loan comp has no id and cannot be updated.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = buildLoanCompPayload(loanComp);
      const response =
        isEdit && loanCompId !== undefined
          ? await apiPut("loan-comp", loanCompId, payload)
          : await apiPost("loan-comps", payload);

      if (response?.success) {
        navigate(isEdit ? `/loan-comps/${loanCompId}` : "/loan-comps");
      } else {
        showErrorFromResponse(response);
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ position: "relative", zIndex: 1 }}>
      <Box sx={{ position: "sticky", zIndex: 10, marginBottom: "10px" }}>
        <SimpleToolbar>
          {isEdit && (
            <CustomButton
              text="Cancel"
              handleButtonPress={() => navigate(`/loan-comps/${loanCompId}`)}
              solid={false}
              isButtonDisabled={false}
              data-position="left"
            />
          )}
          <CustomButton
            text={isEdit ? "Update Loan Comp" : "Create Loan Comp"}
            handleButtonPress={handleSubmit}
            isButtonDisabled={isSaving || !hasRequiredLoanCompFields(loanComp)}
          />
          {isEdit && (
            <CustomButton
              text="Delete Loan Comp"
              isDeleteButton
              handleButtonPress={() => setDialogOpen(true)}
              isButtonDisabled={false}
              data-position="right"
            />
          )}
        </SimpleToolbar>

        <Box sx={{ width: "95%", margin: "0 auto", padding: 4 }}>
          <DynamicForm
            config={loanCompFieldConfig}
            values={loanComp}
            onChange={handleChange}
            optionsMap={{ countries, sectors, loanTypes }}
            requiredFields={LOAN_COMP_REQUIRED_FIELDS}
          />
        </Box>

        {loanCompId !== undefined && (
          <ConfirmDialog
            open={dialogOpen}
            endpoint="loan-comp"
            id={loanCompId}
            title="Delete Loan Comp"
            description="Are you sure you want to delete this loan comp?"
            onClose={() => setDialogOpen(false)}
            onSuccess={() => navigate("/loan-comps")}
          />
        )}
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default CreateLoanComp;
